import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Star } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-28 pb-16 md:pt-32 md:pb-24 overflow-hidden bg-[#c92e34] text-white"
      data-testid="hero-section"
    >
      {/* Background dotted grid */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
        backgroundImage: "radial-gradient(#fff 1.2px, transparent 1.2px)",
        backgroundSize: "22px 22px"
      }} />
      {/* Soft glows */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <div className="absolute top-40 -right-24 w-96 h-96 rounded-full bg-[#282829]/20 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 relative">
        <div className="relative max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20, rotate: 0 }}
            animate={{ opacity: 1, y: 0, rotate: -3 }}
            transition={{ type: "spring", stiffness: 200, damping: 16 }}
            className="sticker mb-5"
            style={{ background: "#fff", color: "#282829" }}
          >
            <Sparkles size={14} className="text-[#c92e34]" />
            Study Abroad · 10+ Years
          </motion.div>

          <h1 className="font-display leading-[0.92] text-5xl sm:text-6xl md:text-7xl lg:text-[6.4rem]" data-testid="hero-heading">
            <span className="block">YOUR</span>
            <span className="block">
              <span className="text-white">GLOBAL</span>{" "}
              <span className="font-handwritten text-[#282829] inline-block -rotate-2">future</span>
            </span>
            <span className="block">STARTS HERE.</span>
          </h1>
          <h2 className="sr-only">Best Study Abroad Consultants in Nagpur, India — Overseas Education Consultancy for USA, UK, Canada, Australia, Ireland & Europe</h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="mt-5 font-display text-xl sm:text-2xl md:text-3xl leading-tight max-w-2xl"
            data-testid="hero-tagline"
          >
            Skip the blah. <span className="font-handwritten text-[#282829] inline-block -rotate-2">Bring the boom.</span>
            <span className="block mt-1 text-white/95 text-lg sm:text-xl md:text-2xl font-semibold">
              Just killer student branding, tailored for world universities.
            </span>
          </motion.p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#inquiry"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-[#282829] hover:bg-white hover:text-[#282829] text-white font-bold text-sm px-5 py-2.5 rounded-full border-2 border-[#282829] transition-colors"
            >
              Book Free Counselling
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
            {[
              { v: "45K+", l: "Students" },
              { v: "350+", l: "Universities" },
              { v: "10+", l: "Years" },
              { v: "$1M+", l: "Scholarships" },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={`bg-white text-[#282829] border-2 border-[#282829] rounded-2xl p-3 text-center ${
                  i % 2 === 0 ? "brutal-shadow-red-sm" : "brutal-shadow-ink"
                }`}
                data-testid={`hero-stat-${i}`}
              >
                <div className="font-anton text-3xl">{s.v}</div>
                <div className="text-[10px] font-bold tracking-widest uppercase text-[#282829]/55 mt-0.5">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Rated badge */}
      <div className="absolute right-6 bottom-6 hidden lg:flex z-10 bg-[#282829] text-white font-anton text-sm tracking-widest px-4 py-2 rounded-full border-2 border-white -rotate-12 items-center gap-1">
        <Star size={14} fill="currentColor" /> RATED 4.9
      </div>
    </section>
  );
}
